import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermissions, type SecurePermissions } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { logger } from "@/telemetry";

import { type SidebarItem } from "./Sidebar";

/**
 * Sidebar menu items hook that uses server-validated permissions
 * This ensures menu items reflect actual user permissions from the backend
 */
export const useSidebarMenuItems = (): {
  menuItems: SidebarItem[];
  isValidating: boolean;
} => {
  const { permissions, isLoading } = usePermissions();
  const { brand } = useBrand();
  const { t } = useTranslation();
  const [menuItems, setMenuItems] = useState<SidebarItem[]>([]);

  useEffect(() => {
    // Build menu items based on validated permissions
    const buildSecureMenuItems = () => {
      logger.info("SidebarMenuItems - Building with secure permissions", {
        workflows: String(permissions.workflows),
        knowledgeHub: String(permissions.knowledge_hub),
        promptLibrary: String(permissions.prompt_library),
        monitoring: String(permissions.monitoring),
        settings: String(permissions.settings),
      });
      const overviewItems = [];

      // Add dashboard as first item if configured to show in sidebar
      if (brand.dashboard?.showInSideBar && brand.dashboard?.url) {
        overviewItems.push({
          key: "dashboard",
          href: `/${brand.dashboard?.urlSegment ?? "dashboard"}`,
          icon: "solar:chart-2-outline",
          img: `/brands/${brand.prefix || "webuild"}/dashboard.svg`,
          title: brand.dashboard.title ? t(brand.dashboard.title) : t("Dashboard"),
        });
      }

      // Only show chat for non-Invest AI brands (chat is always available)
      if (brand.name !== "Invest Ai") {
        overviewItems.push({
          key: "chat",
          href: "/chat",
          icon: "solar:chat-line-linear",
          img: `/brands/${brand.prefix || "webuild"}/pathfinder.svg`,
          title: t(brand.menuItems?.Pathfinder?.name || "Chat"),
        });
      }

      // Check server-validated permissions for each feature
      if (permissions.workflows) {
        overviewItems.push({
          key: "workflows",
          href: "/workflows",
          icon: "solar:widget-2-outline",
          img: `/brands/${brand.prefix || "webuild"}/playbook.svg`,
          title: t(brand.menuItems?.["Workflow"]?.name || "Workflows"),
        });

        if (brand.prefix !== "ngv") {
          overviewItems.push({
            key: "playground",
            href: "/workflows/playground",
            icon: "solar:layers-outline",
            img: `/brands/${brand.prefix || "webuild"}/playground.svg`,
            title: t("Playground"),
          });
        }
      }

      if (permissions.knowledge_hub) {
        overviewItems.push({
          key: "data",
          href: "/datahub",
          icon: "solar:database-linear",
          img: `/brands/${brand.prefix || "webuild"}/knowledge.svg`,
          title: t(brand.menuItems?.["Knowledge Hub"]?.name || "Knowledge Hub"),
        });
      }

      if (permissions.prompt_library) {
        overviewItems.push({
          key: "prompts",
          href: "/prompts",
          icon: "solar:code-bold",
          img: `/brands/${brand.prefix || "webuild"}/prompt.svg`,
          title: t(brand.menuItems?.["Prompt Library"]?.name || "Prompt Library"),
        });
      }

      const settingsItems = [];

      if (permissions.monitoring) {
        settingsItems.push({
          key: "monitoring",
          href: "/metrics",
          title: t("Monitoring"),
          icon: "solar:pie-chart-2-outline",
          img: `/brands/${brand.prefix || "webuild"}/solutions.svg`,
        });
      }

      // Admin page is accessed via the profile dropdown menu, not the sidebar

      const items: SidebarItem[] = [];

      if (overviewItems.length > 0) {
        items.push({
          key: "overview",
          title: t("Overview"),
          items: overviewItems,
        });
      }

      if (settingsItems.length > 0) {
        items.push({
          key: "settings",
          title: t("Settings"),
          items: settingsItems,
        });
      }

      setMenuItems(items);
    };

    buildSecureMenuItems();
  }, [permissions, brand, t]);

  return {
    menuItems,
    isValidating: isLoading,
  };
};

/**
 * Permission guard for individual menu items
 * Use this to double-check permissions before navigation
 */
export const canAccessMenuItem = async (
  itemKey: string,
  permissions: SecurePermissions,
): Promise<boolean> => {
  const permissionMap: Record<string, keyof typeof permissions> = {
    workflows: "workflows",
    knowledge_hub: "knowledge_hub",
    prompts: "prompt_library",
    monitoring: "monitoring",
    settings: "settings",
  };

  const requiredPermission = permissionMap[itemKey];

  if (!requiredPermission) {
    // No specific permission required (e.g., chat)
    return true;
  }

  return permissions[requiredPermission] || false;
};
